export type NavItem = {
    id: string;
    label: string;
    /**
     * The page this entry opens. A group's path is only its landing page for the collapsed rail:
     * filterNavItems moves it to the first entry the user can actually see.
     */
    path: string;
    /** Extra pathname prefixes that should mark this item as active (revamp-ui) */
    activePaths?: string[];
    children?: NavItem[];
    /**
     * A sidebar group (Orders, Grading, …), as in the Hub: a collapsible header over related pages
     * rather than a page of its own. It is active on any page inside it, and it disappears when
     * the user can see none of its children.
     */
    group?: boolean;
    /** Only show this item when the named region feature flag is enabled */
    featureFlag?: string;
    /** Only show this item to users with this role (case-insensitive) */
    requiredRole?: string;
    /** Show this item to users with any of these roles (case-insensitive). Super Admin always passes. */
    requiredRoles?: string[];
    /**
     * A Mystery Slabs team entry (added at runtime, see sidebar/mysteryTeamNav): picking it selects
     * this team — a team id, or "all" — and opens Mystery Slabs.
     */
    mysteryTeam?: string;
};

/*
  One default for every office's portal: Dashboard on its own, then every page filed under a
  group, as in the Hub. Group ids carry a `group-` prefix so none collides with a page's id
  (Orders the group, `orders` the Customer Orders page). Support was a group already and keeps
  its id and paths. Per-office rearranging is a separate feature (office-sidebar-arrangement).
*/
const NAVIGATION_ITEMS: NavItem[] = [
    { id: 'dashboard', label: 'Dashboard', path: '/dashboard' },
    {id: 'group-orders', label: 'Orders', path: '/orders', group: true, children: [
        { id: 'orders', label: 'Orders', path: '/orders' },
        {
            id: 'overdue-orders',
            label: 'Overdue Orders',
            path: '/overdue-orders',
            requiredRoles: ['admin', 'supervisor', 'super admin', 'hq'],
        },
        { id: 'mystery-orders', label: 'Mystery Slabs', path: '/mystery-orders', featureFlag: 'mysterySlabsEnabled' },
    ]},
    {id: 'group-grading', label: 'Grading', path: '/grading-standards', group: true, children: [
        {id: 'grading-standards', label: 'Grading Standards', path: '/grading-standards'},
        // Its own path, not /grading-standards/…: the prefix match would light Grading Standards too.
        {id: 'calibration', label: 'Calibration', path: '/calibration'},
        {id: 'authentication', label: 'Authentication', path: '/authentication'},
        {id: 'labelling',label: 'Labelling',path: '/labelling',},
        // Was the only entry under Tools.
        {id: 'grade-check', label: 'Grade Check', path: '/grade-check', activePaths: ['/grade-check', '/gradeCheck']},
    ]},
    /*
      Every catalog-maintenance tool under one roof: the catalog itself, then what used to be the
      Catalog Dashboard group, led by its Overview. Automation, the Checker and the translation
      tools are open to all roles, while Overview and the Duplicator keep the admin gate of the
      endpoints behind them. A non-admin who lands on /catalog-dashboard is sent on to Catalog
      Automation.
    */
    {id: 'group-catalog', label: 'Catalog', path: '/catalog', group: true, children: [
        { id: 'catalog', label: 'Catalog', path: '/catalog' },
        {id: 'catalog-overview', label: 'Overview', path: '/catalog-dashboard', requiredRoles: ['admin', 'super admin']},
        {id: 'automated', label: 'Catalog Automation', path: '/automated'},
        {id: 'catalog-checker', label: 'Catalogue Checker', path: '/catalog-checker'},
        // Admin-gated to match the endpoints behind it: a merge rewrites shared catalog rows,
        // so a non-admin would see the menu and collect 401s.
        {id: 'catalog-duplicates', label: 'Catalog Duplicator', path: '/catalog-duplicates/sets', activePaths: ['/catalog-duplicates'], requiredRoles: ['admin', 'super admin'], children: [
            {id: 'catalog-duplicates-sets', label: 'Sets', path: '/catalog-duplicates/sets'},
            {id: 'catalog-duplicates-products', label: 'Products', path: '/catalog-duplicates/products'},
            // Lists every office's gradings, so Super Admin only — the endpoints refuse anyone else.
            {id: 'catalog-duplicates-manual-entries', label: 'Manual entries', path: '/catalog-duplicates/manual-entries', requiredRole: 'super admin'},
        ]},
        {id: 'multi-language', label: 'Card Name Translation', path: '/multi-language'},
        {id: 'set-name-translations', label: 'Set Name Translation', path: '/set-name-translations'},
    ]},
    {id: 'group-supplies', label: 'Supplies', path: '/equipment', group: true, children: [
        {id: 'equipment', label: 'Equipment & Ordering', path: '/equipment'},
        {id: 'stock', label: 'Stock Take', path: '/stock'},
    ]},
    {id: 'group-team', label: 'Team', path: '/onboarding', group: true, children: [
        {id: 'onboarding', label: 'Onboarding', path: '/onboarding'},
        {id: 'new-grader', label: 'New Grader', path: '/new-grader'},
    ]},
    {id: 'group-business', label: 'Business', path: '/royalties', group: true, children: [
        {id: 'royalties', label: 'Royalties', path: '/royalties'},
        {id: 'partners', label: 'Partners', path: '/partners'},
        {id: 'marketing', label: 'Marketing', path: '/marketing'},
    ]},
    {id: 'support', label: 'Support', path: '/support', group: true, activePaths: ['/support', '/requests', '/notifications', '/nfc-setup'], children: [
        {id: 'hq-chat', label: 'HQ Chat', path: '/support'},
        {id: 'requests', label: 'Requests', path: '/requests'},
        {id: 'notifications', label: 'Notifications', path: '/notifications'},
        // Its own path, not /support/…: HQ Chat's /support would light up on it too (prefix match).
        {id: 'nfc-setup', label: 'NFC Chip Setup', path: '/nfc-setup'},
    ]},
    // What were the Account and Resources menus: the office's own setup first, then the lookups.
    {id: 'group-settings', label: 'Settings', path: '/account/users', group: true, children: [
        {id:"user", label:"User", path:"/account/users"},
        {id: 'storefront-contact', label: 'Storefront Contact', path: '/account/storefront-contact'},
        {id: 'installed-stores', label: 'Installed Stores', path: '/account/installed-stores', requiredRole: 'super admin'},
        {id: 'audit-history', label: 'Audit History', path: '/audit-history', requiredRoles: ['admin', 'super admin']},
        {id: 'region-settings', label: 'Region Settings', path: '/settings/region-settings', requiredRole: 'super admin'},
        {id: 'workflow-editor', label: 'Workflow Editor', path: '/settings/workflow-editor', requiredRoles: ['admin', 'super admin']},
        {id: 'shopify-variants', label: 'Shopify Variants', path: '/resources/shopify-variants'},
        {id: 'webhook-settings', label: 'Webhook Settings', path: '/resources/webhook-settings'},
        {id: 'customer-lookup', label: 'Customer Lookup', path: '/resources/customer-lookup'},
        {id: 'label', label: 'Label', path: '/label'},
        {id: 'purchaseDetail', label: 'Purchase Detail', path: '/purchaseDetail/purchase'},
        {id: 'loyaltyDetail', label: 'Loyalty Detail', path: '/loyaltyDetail/loyalty'}
    ]},
];

export default NAVIGATION_ITEMS
